<template>
  <b-card>
    <b-row>
      <b-col>
        <h1 class="mb-2">Profile</h1>
      </b-col>
    </b-row>

    <div v-if="isLoading" class="text-center pt-3">
      <b-spinner label="Loading..." />
    </div>

    <b-form v-else class="d-flex flex-column" @submit="onSubmit">
      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="Name" label-for="profile-name">
            <b-form-input id="profile-name" v-model="form.name" required />
          </b-form-group>
          <b-form-group label="Phone" label-for="profile-phone">
            <b-form-input id="profile-phone" v-model="form.phone" required />
          </b-form-group>
          <b-form-group label="Email" label-for="profile-email">
            <b-form-input id="profile-email" v-model="form.email" type="email" required />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Profile Photo" label-for="profile-picture">
            <b-form-file
              id="profile-picture"
              accept=".jpg,.jpeg,.png,.gif,.webp"
              placeholder="Choose a file or drop it here..."
              drop-placeholder="Drop file here..."
              @change="onFileChange"
            />
          </b-form-group>
          <b-img
            thumbnail
            rounded="circle"
            fluid
            :src="image"
            class="profile-image mb-1"
            :style="{ width: '120px', height: '120px', objectFit: 'cover' }"
          />
        </b-col>
      </b-row>

      <b-button type="submit" variant="primary" class="align-self-end mt-1" :disabled="isSaving">
        <b-spinner v-if="isSaving" label="Saving" small />
        <span v-else>Save</span>
      </b-button>
    </b-form>

    <hr class="my-2">

    <div class="d-flex justify-content-end">
      <b-button
        variant="outline-primary"
        :to="{ name: 'password' }"
      >
        Change password
      </b-button>
    </div>
  </b-card>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import {
  BButton,
  BCard,
  BCol,
  BForm,
  BFormFile,
  BFormGroup,
  BFormInput,
  BImg,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import { toastFailure, toastSuccess } from '@/libs/toastification'
import { backendErrorString, hasCustomUserPicture, normalizeImageUrl } from '@/libs/utils'
import { resolvePictureUrl } from '@/libs/pictureUrl'

const store = useStore()
const user = computed(() => store.getters['auth/user'])
const isLoading = ref(true)
const isSaving = ref(false)

const form = reactive({
  name: '',
  email: '',
  phone: '',
  picture: null,
})

const image = ref(resolvePictureUrl(null))

watch(
  user,
  (value) => {
    if (!value) return
    form.name = value.name || ''
    form.email = value.email || ''
    form.phone = value.phone || ''
    image.value = hasCustomUserPicture(value.picture)
      ? normalizeImageUrl(value.picture)
      : resolvePictureUrl(null)
    isLoading.value = false
  },
  { immediate: true }
)

const onFileChange = (event) => {
  const file = event && event.target && event.target.files ? event.target.files[0] : null
  form.picture = file || null
  if (!form.picture) return

  const reader = new FileReader()
  reader.addEventListener('load', () => {
    image.value = reader.result
  })
  reader.readAsDataURL(form.picture)
}

const onSubmit = async (event) => {
  if (event && event.preventDefault) {
    event.preventDefault()
  }
  if (!user.value || !user.value.id) return

  isSaving.value = true
  try {
    const formData = new FormData()
    formData.append('id', user.value.id)
    formData.append('name', form.name)
    formData.append('email', form.email)
    formData.append('phone', form.phone)
    if (form.picture) {
      formData.append('picture', form.picture)
    }
    formData.append('_method', 'POST')

    await store.dispatch('auth/updateUser', formData)
    toastSuccess('Profile updated successfully.')
  } catch (error) {
    toastFailure(backendErrorString(error))
  } finally {
    isSaving.value = false
  }
}
</script>
